import React, { useCallback, useEffect, useState } from "react";
import VMasker from "vanilla-masker";
import { FiUser } from "react-icons/fi";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { Button, Input, LoadingSpinner } from "@components/common";
import { useFeedback } from "@contexts/FeedbackContext";
import { perfilService, Perfil as PerfilData } from "@services/perfilService";
import "./pages-common.css";

/**
 * Aplica máscara de telefone brasileiro conforme o usuário digita.
 * 10 dígitos → (11) 1234-5678 · 11 dígitos → (11) 91234-5678
 */
const mascararTelefone = (valor: string): string => {
  const digitos = valor.replace(/\D/g, "").slice(0, 11);
  return VMasker.toPattern(
    digitos,
    digitos.length > 10 ? "(99) 99999-9999" : "(99) 9999-9999",
  );
};

export const Perfil: React.FC = () => {
  const { showError, showSuccess } = useFeedback();

  const [perfil, setPerfil] = useState<PerfilData | null>(null);
  const [telefone, setTelefone] = useState("");
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erroTelefone, setErroTelefone] = useState<string>();

  const carregar = useCallback(async () => {
    try {
      const dados = await perfilService.obter();
      setPerfil(dados);
      setTelefone(dados.telefone ? mascararTelefone(dados.telefone) : "");
    } catch (err: any) {
      showError(err.message || "Não foi possível carregar seu perfil.");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTelefone(mascararTelefone(e.target.value));
    setErroTelefone(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const digitos = telefone.replace(/\D/g, "");
    if (digitos.length > 0 && (digitos.length < 10 || digitos.length > 11)) {
      setErroTelefone("Informe DDD e número — 10 ou 11 dígitos.");
      return;
    }

    setSalvando(true);
    try {
      // String vazia limpa o telefone cadastrado.
      const atualizado = await perfilService.atualizar({ telefone });
      setPerfil(atualizado);
      setTelefone(
        atualizado.telefone ? mascararTelefone(atualizado.telefone) : "",
      );
      showSuccess("Perfil atualizado com sucesso.");
    } catch (err: any) {
      showError(err.message || "Não foi possível salvar seu perfil.");
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (!perfil) {
    return (
      <div className="alert alert-error max-w-2xl mx-auto">
        <span>Não foi possível carregar seu perfil.</span>
      </div>
    );
  }

  const telefoneOriginal = perfil.telefone
    ? mascararTelefone(perfil.telefone)
    : "";
  const houveMudanca = telefone !== telefoneOriginal;

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-base-content mb-1">Meu perfil</h1>
      <p className="text-sm text-base-content/70 mb-6">
        Seus dados de identificação vêm do Wrike. Aqui você ajusta as
        informações de contato que o sistema usa.
      </p>

      {/* Identidade — somente leitura, sincronizada do Wrike */}
      <div className="card bg-base-100 shadow-sm border border-base-300 mb-4">
        <div className="card-body">
          <div className="flex items-center gap-4">
            {perfil.avatarUrl ? (
              <div className="avatar">
                <div className="w-16 rounded-full">
                  <img src={perfil.avatarUrl} alt={perfil.nome ?? "Avatar"} />
                </div>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shrink-0">
                <FiUser className="h-7 w-7 text-primary-content" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-lg font-semibold text-base-content truncate">
                {perfil.nome || "—"}
              </p>
              <p className="text-sm text-base-content/70 truncate">
                {perfil.email}
              </p>
              <span className="badge badge-ghost badge-sm mt-2">
                {perfil.role}
              </span>
            </div>
          </div>

          <div className="alert alert-info mt-4 py-2">
            <HiOutlineInformationCircle className="h-5 w-5 shrink-0" />
            <span className="text-sm">
              Nome, e-mail e foto são atualizados a partir do Wrike a cada
              login. Para alterá-los, edite seu perfil no Wrike.
            </span>
          </div>
        </div>
      </div>

      {/* Contato — editável */}
      <form onSubmit={handleSubmit}>
        <div className="card bg-base-100 shadow-sm border border-base-300">
          <div className="card-body">
            <h2 className="card-title text-lg">Contato</h2>

            <Input
              label="Telefone"
              type="tel"
              inputMode="tel"
              value={telefone}
              onChange={handleTelefoneChange}
              placeholder="(11) 91234-5678"
              error={erroTelefone}
              helperText="Opcional. Quando preenchido, aparece para o fornecedor junto do seu nome e e-mail nas cotações que você enviar."
              fullWidth
            />

            <div className="card-actions justify-end mt-4">
              <Button
                type="submit"
                loading={salvando}
                disabled={salvando || !houveMudanca}
              >
                Salvar
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Perfil;
